<template>
  <feature-toggle toggle="enableCompetitorSimulation">
    <v-row class="title-row">
      <v-col cols="12">
        {{ $t('workPackages.costChangeThresholds.label') }}
      </v-col>
    </v-row>
    <v-row class="input-row">
      <v-col cols="6" class="pt-0 pb-0 pr-2">
        <v-text-field
          v-model="costChangeThresholdMinDisplay"
          :label="$t('workPackages.costChangeThresholds.min')"
          :disabled="disabled"
          type="text"
          @blur="formatInputOnBlur"
          @keypress="ensureDecimal($event, true)"
          @paste="ensurePastedDecimal($event, true)"
        />
      </v-col>
      <v-col cols="6" class="pt-0 pb-0 pl-2">
        <v-text-field
          v-model="costChangeThresholdMaxDisplay"
          :label="$t('workPackages.costChangeThresholds.max')"
          :disabled="disabled"
          type="text"
          @blur="formatInputOnBlur"
          @keypress="ensureDecimal($event, true)"
          @paste="ensurePastedDecimal($event, true)"
        />
      </v-col>
    </v-row>
    <v-row v-if="validationError" no-gutters>
      <v-col cols="12" class="pa-2">
        <div class="error--text text-body2">
          {{ $t('workPackages.costChangeThresholds.errors.minGreaterThanMax') }}
        </div>
      </v-col>
    </v-row>
  </feature-toggle>
</template>

<script>
import { get, isNil } from 'lodash';
import formatNumberMixin from '@/js/mixins/formatNumber';
import ensureNumberMixin from '@/js/mixins/ensureNumber';
import numberFormats from '@enums/number-formats';

export default {
  mixins: [formatNumberMixin, ensureNumberMixin],

  props: {
    workpackage: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    costChangeThresholdMin: {
      get() {
        const value = get(this.workpackage, 'costChangeThresholds.min');
        return isNil(value) ? 0 : value;
      },
      set(value) {
        if (!this.workpackage.costChangeThresholds) {
          this.$set(this.workpackage, 'costChangeThresholds', {});
        }
        this.$set(this.workpackage.costChangeThresholds, 'min', isNil(value) ? 0 : value);
      },
    },

    costChangeThresholdMinDisplay: {
      get() {
        return this.formatNumber({
          number: this.costChangeThresholdMin,
          format: numberFormats.decimal,
        });
      },
      set(value) {
        this.costChangeThresholdMin = this.formatStringToNumber(value) || 0;
      },
    },

    costChangeThresholdMax: {
      get() {
        const value = get(this.workpackage, 'costChangeThresholds.max');
        return isNil(value) ? 0 : value;
      },
      set(value) {
        if (!this.workpackage.costChangeThresholds) {
          this.$set(this.workpackage, 'costChangeThresholds', {});
        }
        this.$set(this.workpackage.costChangeThresholds, 'max', isNil(value) ? 0 : value);
      },
    },

    costChangeThresholdMaxDisplay: {
      get() {
        return this.formatNumber({
          number: this.costChangeThresholdMax,
          format: numberFormats.decimal,
        });
      },
      set(value) {
        this.costChangeThresholdMax = this.formatStringToNumber(value) || 0;
      },
    },

    /** True when min exceeds max. */
    validationError() {
      const min = this.costChangeThresholdMin;
      const max = this.costChangeThresholdMax;
      return min > max;
    },
  },

  watch: {
    /** Inform the parent whenever validation state changes, including on mount. */
    validationError: {
      immediate: true,
      handler(hasError) {
        this.$emit('validation-change', hasError);
      },
    },
  },

  methods: {
    /**
     * Format input on blur - resets empty fields and ensures proper formatting.
     * Strips trailing separators and re-formats the number.
     * @param {FocusEvent} event
     */
    formatInputOnBlur(event) {
      if (event.target.value === '' || event.target.value === '-') {
        // eslint-disable-next-line no-param-reassign
        event.target.value = this.formatNumber({ number: 0, format: numberFormats.decimal });
      } else {
        // Parse the input and re-format to strip trailing separators
        const parsedValue = this.formatStringToNumber(event.target.value);
        if (parsedValue !== null && !Number.isNaN(parsedValue)) {
          // eslint-disable-next-line no-param-reassign
          event.target.value = this.formatNumber({
            number: parsedValue,
            format: numberFormats.decimal,
          });
        }
      }
      // Update the model which will trigger the display formatting
      this.$nextTick(() => {
        event.target.dispatchEvent(new Event('input'));
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.title-row {
  font-weight: normal;
  font-size: 1.4rem;
}

.input-row {
  font-size: small;
}

.button-row {
  font-size: small;
  margin-top: 0.5rem;
}
</style>
